import React, { useEffect, useRef, useState } from "react";
import { client } from "../../..";
import { WA_RECORD_SEARCH } from "../../../Queries/WhatsApp";
import { gqlErrorMessage } from "./waUtils";

const DEBOUNCE_MS = 300;

/**
 * Search leads + clients and pick a record (optionally a specific contact).
 * onPick({ kind, id, title, contact }) — contact may be null when
 * `requireContact` is false and the user picks the record itself.
 */
const RecordSearch = ({ onPick, requireContact = true, selected, autoFocus = true, placeholder }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const reqRef = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setError(null);
      return undefined;
    }
    const reqId = ++reqRef.current;
    const t = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await client.query({ query: WA_RECORD_SEARCH, variables: { query: q }, fetchPolicy: "network-only" });
        if (reqId === reqRef.current) setResults(data.whatsappRecordSearch || []);
      } catch (err) {
        if (reqId === reqRef.current) setError(gqlErrorMessage(err));
      } finally {
        if (reqId === reqRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  return (
    <div>
      <div className="wa-search" style={{ marginBottom: 12 }}>
        <i className={loading ? "fa-solid fa-spinner wa-spin" : "fa-solid fa-magnifying-glass"} />
        <input
          className="wa-input"
          autoFocus={autoFocus}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder || "Search agency, client, contact name, phone or email"}
          aria-label="Search leads and clients"
        />
      </div>
      {error && <div className="wa-alert wa-alert--danger" style={{ marginBottom: 10 }}>{error}</div>}
      {!loading && query.trim().length >= 2 && !results.length && !error && (
        <div className="wa-help" style={{ textAlign: "center", padding: 16 }}>No leads or clients match "{query.trim()}".</div>
      )}
      {results.map((r) => (
        <div className="wa-result" key={`${r.kind}-${r.id}`}>
          <div className="wa-result__head">
            <span className={`wa-chip ${r.kind === "lead" ? "wa-chip--accent" : "wa-chip--ok"}`}>{r.kind === "lead" ? "Lead" : "Client"}</span>
            <b>{r.title}</b>
            {!requireContact && (
              <button type="button" className="wa-btn wa-btn--sm" onClick={() => onPick({ kind: r.kind, id: r.id, title: r.title, contact: null })}>
                Select
              </button>
            )}
          </div>
          {r.subtitle && <div className="wa-help" style={{ padding: "0 12px 6px", margin: 0 }}>{r.subtitle}</div>}
          {(r.contacts || []).map((c) => {
            const isSel = selected && selected.kind === r.kind && selected.id === r.id && selected.contact?.id === c.id;
            return (
              <div className={`wa-result__contact ${isSel ? "is-selected" : ""}`} key={c.id}>
                <div>
                  <b>{c.name || "Unnamed contact"}</b>{c.title ? <span className="wa-help"> · {c.title}</span> : null}
                  <small>{c.displayNumber || (c.phone ? `${c.phone} (not a valid WhatsApp number)` : "No phone on file")}{c.email ? ` · ${c.email}` : ""}</small>
                </div>
                <button
                  type="button"
                  className={`wa-btn wa-btn--sm ${isSel ? "wa-btn--primary" : ""}`}
                  disabled={requireContact && !c.waId}
                  onClick={() => onPick({ kind: r.kind, id: r.id, title: r.title, contact: c })}
                >
                  {isSel ? <><i className="fa-solid fa-check" /> Selected</> : "Select"}
                </button>
              </div>
            );
          })}
          {!(r.contacts || []).length && <div className="wa-result__contact"><small>No contacts on this record.</small></div>}
        </div>
      ))}
    </div>
  );
};

export default RecordSearch;
